import type { ReactElement } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useGetMyProfileQuery } from '../../../services/ProfileService';
import { hasPhysicianRequestsAccess } from '../../profile/utils/profileUtils';

/**
 * Route-level authorization guard for the PhysicianRequests-claim-gated queue and review routes
 * (US1, spec 121, T024).
 *
 * Mounts `children` ONLY once the profile has resolved AND the user has the **PhysicianRequests**
 * claim, so an unauthorized user's page shell/data hooks never mount (no partial rendering, no
 * queue or review query firing). Shows a neutral loading state while the profile is unresolved to
 * avoid a redirect flash; denies direct navigation otherwise.
 */
export function RequirePhysicianRequestsAccess({ children }: { children: ReactElement }): ReactElement | null {
  const { data: profile, isLoading } = useGetMyProfileQuery();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress data-testid="physician-requests-auth-loading" />
      </Box>
    );
  }

  if (!hasPhysicianRequestsAccess(profile))
    return <Navigate to="/" replace />;

  return children;
}
